import {
  COST_ENTRY_KINDS,
  COST_ENTRY_SORT_FIELDS,
  COST_ENTRY_STATUSES,
  type CostEntryListQuery,
  HEALTH_LEVELS,
  MATERIAL_ISSUE_KINDS,
  MATERIAL_ISSUE_SORT_FIELDS,
  MATERIAL_ISSUE_STATUSES,
  type MaterialIssueListQuery,
  PROJECT_FACT_SORT_FIELDS,
  PROJECT_FACT_SOURCES,
  PROJECT_FINANCIAL_GROUPINGS,
  PROJECT_KINDS,
  PROJECT_PRIORITIES,
  PROJECT_SORT_FIELDS,
  PROJECT_STATUSES,
  PROJECT_VIEWS,
  type ProjectFactsQuery,
  type ProjectListQuery,
  type ProjectSummaryQuery,
  SIGNAL_CODES,
} from '@sahab/contracts'
import { z } from 'zod'

type ProjectSortField = (typeof PROJECT_SORT_FIELDS)[number]
export type ProjectSort = ProjectSortField | `-${ProjectSortField}`

export const PROJECT_PAGE_SIZES = [25, 50, 100] as const
/** Newest first: the latest number on top. */
export const DEFAULT_PROJECT_SORT: ProjectSort = '-code'
export const PROJECT_LAYOUTS = ['list', 'board', 'timeline'] as const
export type ProjectLayout = (typeof PROJECT_LAYOUTS)[number]
export const TIMELINE_GROUPS = ['none', 'manager', 'customer'] as const
export type TimelineGrouping = (typeof TIMELINE_GROUPS)[number]

export function isProjectSort(value: string): value is ProjectSort {
  const field = value.startsWith('-') ? value.slice(1) : value
  return (PROJECT_SORT_FIELDS as readonly string[]).includes(field)
}

const uuid = z.uuid().optional().catch(undefined)
const page = z.number().int().min(1).optional().catch(undefined)
const pageSize = z
  .number()
  .refine((n): n is (typeof PROJECT_PAGE_SIZES)[number] =>
    (PROJECT_PAGE_SIZES as readonly number[]).includes(n),
  )
  .optional()
  .catch(undefined)
const zoom = z.enum(['month', 'quarter']).optional().catch(undefined)

/**
 * The portfolio (`/projects`) lives in the URL: the view tab, layout, filters, sort, page, the
 * timeline's scale and grouping, and the New project drawer (`new`: blank or from a type).
 */
export const projectPortfolioSearchSchema = z.object({
  view: z.enum(PROJECT_VIEWS).optional().catch(undefined),
  layout: z.enum(PROJECT_LAYOUTS).optional().catch(undefined),
  page,
  pageSize,
  sort: z.string().refine(isProjectSort).optional().catch(undefined),
  q: z.coerce.string().trim().max(200).optional().catch(undefined),
  projectStatus: z.enum(PROJECT_STATUSES).optional().catch(undefined),
  kind: z.enum(PROJECT_KINDS).optional().catch(undefined),
  typeId: uuid,
  managerId: uuid,
  customerId: uuid,
  orgUnitId: uuid,
  priority: z.enum(PROJECT_PRIORITIES).optional().catch(undefined),
  /** The stored health (P5): projects measured at this level, or carrying this signal. */
  health: z.enum(HEALTH_LEVELS).optional().catch(undefined),
  signal: z.enum(SIGNAL_CODES).optional().catch(undefined),
  zoom,
  group: z.enum(TIMELINE_GROUPS).optional().catch(undefined),
  /** Opens the New project drawer: `blank`, `type` (with `newTypeId` when one is chosen) or `quotation`. */
  new: z.enum(['blank', 'type', 'quotation']).optional().catch(undefined),
  newTypeId: uuid,
})
export type ProjectPortfolioSearch = z.infer<typeof projectPortfolioSearchSchema>

export const PROJECT_TABS = [
  'overview',
  'plan',
  'tasks',
  'budget',
  'billing',
  'contract',
  'time',
  'team',
  'files',
  'activity',
] as const
export type ProjectTab = (typeof PROJECT_TABS)[number]

/** The Contract tab's sub-tabs (projects/06 §5.8); `contracting/queries.ts` lists the deep links. */
export const CONTRACT_VIEWS = ['boq', 'variations', 'claims', 'retention', 'bonds', 'final'] as const
export type ContractView = (typeof CONTRACT_VIEWS)[number]

/** The workspace (`/projects/$projectId`): the open tab, the plan's view and the node drawer. */
export const projectWorkspaceSearchSchema = z.object({
  tab: z.enum(PROJECT_TABS).optional().catch(undefined),
  planView: z.enum(['table', 'timeline']).optional().catch(undefined),
  zoom,
  /** A plan node to open in its drawer (a milestone from the Overview). */
  node: uuid,
  /** Billing: tick every billable unbilled line as the tab opens ("Invoice unbilled costs"). */
  billingSelect: z.enum(['unbilled']).optional().catch(undefined),
  tasksLayout: z.enum(['list', 'board']).optional().catch(undefined),
  /** The Tasks tab's list grouped by where each task sits in the plan. */
  tasksGroup: z.enum(['node']).optional().catch(undefined),
  /** Budget & cost: how the figures are grouped (by category when absent). */
  budgetView: z.enum(PROJECT_FINANCIAL_GROUPINGS).optional().catch(undefined),
  /** Opens the budget editor on this version (approvals link here). */
  budget: uuid,
  /** Cost lines: posted costs (absent) or the commitments of draft purchases (D23). */
  costKind: z.enum(['COMMITMENT']).optional().catch(undefined),
  /** Cost lines: the filters, sort and page of the tab's table. */
  costNode: z
    .union([z.uuid(), z.literal('none')])
    .optional()
    .catch(undefined),
  costCategory: uuid,
  costSource: z.enum(PROJECT_FACT_SOURCES).optional().catch(undefined),
  costFrom: z.iso.date().optional().catch(undefined),
  costTo: z.iso.date().optional().catch(undefined),
  costPage: page,
  costSort: z
    .string()
    .refine((v) => sortField(v, PROJECT_FACT_SORT_FIELDS))
    .optional()
    .catch(undefined),
  /** Contract: the sub-tab (BOQ when absent). */
  contractView: z.enum(CONTRACT_VIEWS).optional().catch(undefined),
  /** Contract › BOQ: the version shown (the CONTRACT one, else the newest draft, when absent). */
  boqVersion: uuid,
  /** Contract › BOQ: the current BOQ (contract ± approved variations, with certified to date). */
  boqView: z.enum(['current']).optional().catch(undefined),
  /** Contract › BOQ: the language of the descriptions (the UI's when absent). */
  boqLang: z.enum(['en', 'ar']).optional().catch(undefined),
  /** Contract › Variations: the variation open in its drawer. */
  variation: uuid,
  /** Contract › Claims: the claim to open, and the command to start on it (KW2's screens). */
  claim: uuid,
  claimAction: z.enum(['new', 'submit', 'certify']).optional().catch(undefined),
  /**
   * Contract › Retention & advance: open the release of the stage that is due, the forfeit, or the
   * advance reduction (a FINAL certificate stuck on the advance links here, P5).
   */
  retentionAction: z.enum(['release', 'forfeit', 'reduce']).optional().catch(undefined),
  /** Contract › Bonds: the guarantee open in its drawer (`new` for a new one). */
  guarantee: z
    .union([z.uuid(), z.literal('new')])
    .optional()
    .catch(undefined),
  /** Time (P6): the period shown (the last four weeks when absent). */
  timeFrom: z.iso.date().optional().catch(undefined),
  timeTo: z.iso.date().optional().catch(undefined),
})
export type ProjectWorkspaceSearch = z.infer<typeof projectWorkspaceSearchSchema>

const FILTER_KEYS = [
  'projectStatus',
  'kind',
  'typeId',
  'managerId',
  'customerId',
  'orgUnitId',
  'priority',
  'health',
  'signal',
] as const

export function hasProjectFilters(search: ProjectPortfolioSearch): boolean {
  return countProjectFilters(search) > 0
}

/** How many filters are set (the phone's Filters button shows it; search is not a filter). */
export function countProjectFilters(search: ProjectPortfolioSearch): number {
  return FILTER_KEYS.filter((key) => search[key] !== undefined).length
}

/** The filters the summary, the list and the timeline share. */
export function toProjectFilters(search: ProjectPortfolioSearch): ProjectSummaryQuery {
  return {
    q: search.q,
    status: search.projectStatus,
    kind: search.kind,
    typeId: search.typeId,
    managerId: search.managerId,
    customerId: search.customerId,
    orgUnitId: search.orgUnitId,
    priority: search.priority,
    health: search.health,
    signal: search.signal,
  }
}

export function toProjectListQuery(
  search: ProjectPortfolioSearch,
  view: NonNullable<ProjectListQuery['view']>,
): ProjectListQuery {
  return {
    ...toProjectFilters(search),
    view,
    page: search.page ?? 1,
    pageSize: search.pageSize ?? 25,
    // Needs attention lists the worst first (the API's own order for the view, P5) until a column
    // is chosen; every other view starts with the newest code.
    sort: search.sort ?? (view === 'attention' ? undefined : DEFAULT_PROJECT_SORT),
  }
}

function sortField(value: string, fields: readonly string[]): boolean {
  return fields.includes(value.startsWith('-') ? value.slice(1) : value)
}

export const FACT_PAGE_SIZE = 25
export const DEFAULT_FACT_SORT = '-factDate'

/** The cost lines' query from the workspace URL. */
export function toFactsQuery(search: ProjectWorkspaceSearch): ProjectFactsQuery {
  return {
    page: search.costPage ?? 1,
    pageSize: FACT_PAGE_SIZE,
    sort: search.costSort ?? DEFAULT_FACT_SORT,
    kind: search.costKind ?? 'COST',
    sourceType: search.costSource,
    costCategoryId: search.costCategory,
    wbsNodeId: search.costNode && search.costNode !== 'none' ? search.costNode : undefined,
    noNode: search.costNode === 'none' ? 'true' : undefined,
    from: search.costFrom,
    to: search.costTo,
  }
}

// ── material issues and cost entries (global lists) ──────────────────────────

export const COST_LIST_PAGE_SIZES = [25, 50, 100] as const
const listPageSize = z
  .number()
  .refine((n): n is (typeof COST_LIST_PAGE_SIZES)[number] =>
    (COST_LIST_PAGE_SIZES as readonly number[]).includes(n),
  )
  .optional()
  .catch(undefined)
const text = z.coerce.string().trim().max(200).optional().catch(undefined)
const date = z.iso.date().optional().catch(undefined)

export const DEFAULT_ISSUE_SORT = '-issueDate'
export const materialIssueSearchSchema = z.object({
  page,
  pageSize: listPageSize,
  sort: z
    .string()
    .refine((v) => sortField(v, MATERIAL_ISSUE_SORT_FIELDS))
    .optional()
    .catch(undefined),
  q: text,
  projectId: uuid,
  warehouseId: uuid,
  kind: z.enum(MATERIAL_ISSUE_KINDS).optional().catch(undefined),
  status: z.enum(MATERIAL_ISSUE_STATUSES).optional().catch(undefined),
  from: date,
  to: date,
})
export type MaterialIssueSearch = z.infer<typeof materialIssueSearchSchema>

export function toMaterialIssueQuery(search: MaterialIssueSearch): MaterialIssueListQuery {
  return {
    page: search.page ?? 1,
    pageSize: search.pageSize ?? 25,
    sort: search.sort ?? DEFAULT_ISSUE_SORT,
    q: search.q,
    projectId: search.projectId,
    warehouseId: search.warehouseId,
    kind: search.kind,
    status: search.status,
    from: search.from,
    to: search.to,
  }
}

export const DEFAULT_ENTRY_SORT = '-entryDate'
export const costEntrySearchSchema = z.object({
  page,
  pageSize: listPageSize,
  sort: z
    .string()
    .refine((v) => sortField(v, COST_ENTRY_SORT_FIELDS))
    .optional()
    .catch(undefined),
  q: text,
  projectId: uuid,
  kind: z.enum(COST_ENTRY_KINDS).optional().catch(undefined),
  status: z.enum(COST_ENTRY_STATUSES).optional().catch(undefined),
  from: date,
  to: date,
})
export type CostEntrySearch = z.infer<typeof costEntrySearchSchema>

export function toCostEntryQuery(search: CostEntrySearch): CostEntryListQuery {
  return {
    page: search.page ?? 1,
    pageSize: search.pageSize ?? 25,
    sort: search.sort ?? DEFAULT_ENTRY_SORT,
    q: search.q,
    projectId: search.projectId,
    kind: search.kind,
    status: search.status,
    from: search.from,
    to: search.to,
  }
}
